import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Loader, CheckCircle, AlertCircle, Download, X } from 'lucide-react';
import { ENDPOINTS } from '../shared/endpoints';
import { post } from '../shared/apiClient';
import { HttpError } from '../shared/httpTypes';

interface ExtractionResult {
  extraction_id: string;
  rules_extracted: number;
  rules: Array<{
    category: string;
    rule_type: string;
    description: string;
    country: string;
    tax_year: number;
    filing_status?: string;
    income_min?: number;
    income_max?: number;
    tax_rate?: number;
  }>;
  summary?: {
    total_rules: number;
    categories: Record<string, number>;
    countries: Record<string, number>;
    years: Record<string, number>;
  };
}

const Extraction: React.FC = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [result, setResult] = useState<ExtractionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveToDatabase, setSaveToDatabase] = useState(true);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf' && !selectedFile.name.endsWith('.pdf')) {
        setError('Please upload a PDF file');
        return;
      }
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  }, []);

  const handleExtract = useCallback(async () => {
    if (!file) {
      setError('Please select a PDF file to extract');
      return;
    }

    setIsExtracting(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (saveToDatabase) {
        formData.append('save_to_database', 'true');
      }

      const response = await post<FormData, ExtractionResult>(
        saveToDatabase ? ENDPOINTS.extraction.extractAndSave : ENDPOINTS.extraction.uploadFile,
        formData
      );

      setResult(response.data);
    } catch (err) {
      if (err instanceof HttpError) {
        const errorMessage = err.data && typeof err.data === 'object' && 'detail' in err.data
          ? (err.data.detail as any)?.message || err.message
          : err.message;
        setError(errorMessage);
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred during extraction.');
      }
      console.error('Extraction error:', err);
    } finally {
      setIsExtracting(false);
    }
  }, [file, saveToDatabase]);

  const handleDownloadCSV = useCallback(async () => {
    if (!result?.extraction_id) return;

    try {
      // Use the API client's get function which handles blob responses
      const { get } = await import('../shared/apiClient');
      const url = `${ENDPOINTS.extraction.downloadCsv}/${result.extraction_id}`;
      
      const response = await get<Blob>(url);
      
      if (response.data instanceof Blob) {
        const downloadUrl = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `tax_rules_extraction_${result.extraction_id}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Download error:', err);
      if (err instanceof HttpError) {
        setError(err.message || 'Failed to download CSV file');
      } else {
        setError('Failed to download CSV file');
      }
    }
  }, [result]);

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <X className="w-4 h-4" />
          <span>Back to Dashboard</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Tax Rule Extraction</h1>
        <p className="text-gray-600">
          Upload a PDF tax document to extract tax rules and save them to the database
        </p>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center space-x-3 mb-4">
          <Upload className="w-6 h-6 text-primary-600" />
          <h2 className="text-xl font-semibold text-gray-900">Upload PDF Document</h2>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select PDF File
          </label>
          <div className="flex items-center space-x-4">
            <label className="flex-1 cursor-pointer">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="hidden"
                disabled={isExtracting}
              />
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                {file ? (
                  <div className="flex items-center justify-center space-x-2">
                    <FileText className="w-8 h-8 text-primary-600" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600">Click to select PDF file</p>
                    <p className="text-sm text-gray-500 mt-1">or drag and drop</p>
                  </div>
                )}
              </div>
            </label>
          </div>
        </div>

        <div className="mb-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={saveToDatabase}
              onChange={(e) => setSaveToDatabase(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">
              Save extracted rules to database
            </span>
          </label>
        </div>

        <button
          onClick={handleExtract}
          disabled={!file || isExtracting}
          className={`w-full px-6 py-3 rounded-md font-medium transition-colors ${
            !file || isExtracting
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-primary-600 text-white hover:bg-primary-700'
          }`}
        >
          {isExtracting ? (
            <span className="flex items-center justify-center space-x-2">
              <Loader className="w-5 h-5 animate-spin" />
              <span>Extracting Rules...</span>
            </span>
          ) : (
            <span className="flex items-center justify-center space-x-2">
              <FileText className="w-5 h-5" />
              <span>Extract Tax Rules</span>
            </span>
          )}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Results Display */}
      {result && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                Extraction Complete
              </h2>
            </div>
            {result.extraction_id && (
              <button
                onClick={handleDownloadCSV}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Download CSV</span>
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">Rules Extracted</p>
              <p className="text-2xl font-bold text-blue-900">{result.rules_extracted}</p>
            </div>
            {result.summary && (
              <>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Categories</p>
                  <p className="text-2xl font-bold text-green-900">
                    {Object.keys(result.summary.categories).length}
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Countries</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {Object.keys(result.summary.countries).length}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Rules List */}
          {result.rules && result.rules.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Extracted Rules ({result.rules.length})
              </h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {result.rules.map((rule, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                            {rule.category}
                          </span>
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                            {rule.rule_type}
                          </span>
                          {rule.country && (
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded">
                              {rule.country}
                            </span>
                          )}
                          {rule.tax_year && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded">
                              {rule.tax_year}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-900 font-medium mb-1">{rule.description}</p>
                        {rule.filing_status && (
                          <p className="text-sm text-gray-600">
                            Filing Status: {rule.filing_status}
                          </p>
                        )}
                        {(rule.income_min !== undefined || rule.income_max !== undefined) && (
                          <p className="text-sm text-gray-600">
                            Income Range: {rule.income_min ?? 0} - {rule.income_max ?? '∞'}
                          </p>
                        )}
                        {rule.tax_rate !== undefined && (
                          <p className="text-sm text-gray-600">
                            Tax Rate: {rule.tax_rate}%
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary Details */}
          {result.summary && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.keys(result.summary.categories).length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Categories:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(result.summary.categories).map(([cat, count]) => (
                        <span key={cat} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {cat}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {Object.keys(result.summary.countries).length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Countries:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(result.summary.countries).map(([country, count]) => (
                        <span key={country} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                          {country}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Extraction;

